import type { FC, ReactNode } from 'react';

import { cn } from '@/utils/cn.util';

export type MetricAccent = 'brand' | 'success' | 'info' | 'warn' | 'danger' | 'pink';

export type MetricChangeTone = 'up' | 'down' | 'neutral';

interface MetricCardProps {
  icon: ReactNode;
  label: string;
  /** Pre-formatted by the caller — money, counts and percentages each render differently. */
  value: string;
  accent?: MetricAccent;
  change?: string;
  changeTone?: MetricChangeTone;
  /** Makes the card actionable. Used by cards that lead into a queue. */
  onClick?: () => void;
  className?: string;
}

// Accent colours resolve to @theme tokens, never to the prototype's orange palette.
const accentVar: Record<MetricAccent, string> = {
  brand: 'var(--color-brand-500)',
  success: 'var(--color-success)',
  info: 'var(--color-info)',
  warn: 'var(--color-warn)',
  danger: 'var(--color-danger)',
  pink: 'var(--color-pink)',
};

const changeToneClass: Record<MetricChangeTone, string> = {
  up: 'text-[var(--color-success-ink)]',
  down: 'text-[var(--color-danger-ink)]',
  neutral: 'text-[var(--color-ink-soft)]',
};

/**
 * Ported from the prototype's `.metric-card`: 20px icon, 11.5px uppercase label at 0.5px
 * tracking, 26px extra-bold value at -1px tracking, and an 80px accent blob bled off the
 * top-right corner at 6% opacity.
 */
const MetricCard: FC<MetricCardProps> = ({
  icon,
  label,
  value,
  accent = 'brand',
  change,
  changeTone = 'neutral',
  onClick,
  className,
}) => {
  const shell = cn(
    'relative overflow-hidden rounded-2xl border border-[var(--color-divider)] bg-white p-5 px-[22px]',
    'shadow-[var(--shadow-card)] transition-all hover:-translate-y-0.5 hover:shadow-[var(--shadow-card-hover)]',
    onClick && 'cursor-pointer text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
    className,
  );

  const body = (
    <>
      <span
        className="pointer-events-none absolute right-0 top-0 h-20 w-20 translate-x-5 -translate-y-5 rounded-full opacity-[0.06]"
        style={{ background: accentVar[accent] }}
      />
      <div className="mb-3 text-xl">{icon}</div>
      <div className="mb-1.5 text-[11.5px] font-semibold uppercase tracking-[0.5px] text-[var(--color-ink-muted)]">
        {label}
      </div>
      <div className="text-[26px] font-extrabold leading-none tracking-[-1px] text-[var(--color-ink)]">
        {value}
      </div>
      {change ? (
        <div className={cn('mt-1.5 text-[11.5px] font-semibold', changeToneClass[changeTone])}>
          {change}
        </div>
      ) : null}
    </>
  );

  // A card that navigates must be a real button, not a div with a click handler — keyboard
  // users and screen readers get nothing from the latter.
  return onClick ? (
    <button type="button" onClick={onClick} className={shell}>
      {body}
    </button>
  ) : (
    <div className={shell}>{body}</div>
  );
};

export default MetricCard;
